/**
 * CROEV Quiz V1 - Quiz Engine
 * Norse Organics - Acne Killer Quiz
 * 
 * Features:
 * - Multi-step navigation with state management
 * - Single and multi-select question handling
 * - Auto-advance for single-select questions
 * - Loading animations with timed transitions
 * - Dynamic content population based on answers
 * - URL parameter generation for redirect
 * - localStorage backup of answers
 */

(function() {
  'use strict';

  // Age mapping for calculations
  const AGE_MAP = {
    'under_35': 30,
    '35_44': 40,
    '45_54': 50,
    '55_64': 60,
    'over_65': 70
  };

  // Age order for finding younger age (for comparison step)
  const AGE_ORDER = ['under_35', '35_44', '45_54', '55_64', 'over_65'];

  // Problem area labels for display
  const PROBLEM_LABELS = {
    'teen_breakouts': 'Teen Breakouts',
    'persistent_acne': 'Persistent Acne',
    'inflamed_skin': 'Inflamed & Red Skin',
    'hormonal_cystic': 'Hormonal/Cystic Acne',
    'dark_marks': 'Dark Marks & Scars',
    'confidence_acne': 'Confidence Issues'
  };

  class CroevQuiz {
    constructor(container) {
      this.container = container;
      this.sectionId = container.dataset.sectionId;
      this.redirectUrl = container.dataset.redirectUrl;
      this.redirectUrlMale = container.dataset.redirectUrlMale || '';
      this.redirectUrlFemale = container.dataset.redirectUrlFemale || '';
      
      // Get quiz data from JSON
      const dataEl = document.getElementById(`croev-quiz-data-${this.sectionId}`);
      this.config = dataEl ? JSON.parse(dataEl.textContent) : {};
      
      // State
      this.state = {
        currentStep: 0,
        answers: {},
        history: []
      };
      
      // DOM elements
      this.steps = Array.from(container.querySelectorAll('.croev-quiz__step'));
      this.progressBar = container.querySelector('.croev-quiz__progress-bar');
      this.progressContainer = container.querySelector('.croev-quiz__progress');
      
      // Timers
      this.autoAdvanceTimer = null;
      this.checklistTimers = [];
      
      this.init();
    }

    init() {
      // Clear saved state on fresh page load (start quiz fresh each time)
      this.clearState();

      // Bind events
      this.bindEvents();

      // Show first step
      this.showStep(this.state.currentStep);

      // Update progress
      this.updateProgress();
    }

    bindEvents() {
      // Option selection
      this.container.addEventListener('click', (e) => {
        const option = e.target.closest('.croev-quiz__option');
        if (option) {
          this.handleOptionSelect(option);
        }
        
        const continueBtn = e.target.closest('[data-action="continue"]');
        if (continueBtn) {
          this.nextStep();
        }
        
        const backBtn = e.target.closest('[data-action="back"]');
        if (backBtn) {
          this.prevStep();
        }
        
        const claimBtn = e.target.closest('[data-action="claim-offer"]');
        if (claimBtn) {
          this.handleClaimOffer(claimBtn);
        }

        const skipBtn = e.target.closest('[data-action="skip"]');
        if (skipBtn) {
          this.handleSkip(skipBtn);
        }
      });

      // Keyboard navigation
      this.container.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          const option = e.target.closest('.croev-quiz__option');
          if (option) {
            e.preventDefault();
            this.handleOptionSelect(option);
          }
        }
      });

      // Input field handling
      this.container.addEventListener('input', (e) => {
        const input = e.target.closest('.croev-quiz__input');
        if (input) {
          this.handleInputChange(input);
        }
      });

      // Input field Enter key to continue
      this.container.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          const input = e.target.closest('.croev-quiz__input');
          if (input) {
            const step = input.closest('.croev-quiz__step');
            const continueBtn = step?.querySelector('[data-action="continue"]');
            if (continueBtn && !continueBtn.disabled) {
              this.nextStep();
            }
          }
        }
      });
    }

    handleInputChange(input) {
      const step = input.closest('.croev-quiz__step');
      const questionId = step.dataset.questionId;
      const value = input.value.trim();
      const inputType = input.type;
      const min = input.min ? parseInt(input.min, 10) : null;
      const max = input.max ? parseInt(input.max, 10) : null;
      const required = input.hasAttribute('required');

      // Validate
      let isValid = true;
      const errorEl = step.querySelector('.croev-quiz__input-error');

      if (required && !value) {
        isValid = false;
      } else if (inputType === 'number' && value) {
        const numVal = parseInt(value, 10);
        if (isNaN(numVal)) {
          isValid = false;
        } else if (min !== null && numVal < min) {
          isValid = false;
        } else if (max !== null && numVal > max) {
          isValid = false;
        }
      } else if (inputType === 'email' && value) {
        isValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
      }

      // Update UI
      input.classList.toggle('is-invalid', !isValid && value.length > 0);
      if (errorEl) {
        errorEl.hidden = isValid || value.length === 0;
      }

      // Store answer if valid
      if (isValid && value) {
        this.state.answers[questionId] = { value, label: value };
        this.saveState();
      } else {
        delete this.state.answers[questionId];
      }

      // Enable/disable continue button
      const continueBtn = step.querySelector('[data-action="continue"]');
      if (continueBtn) {
        continueBtn.disabled = !isValid || !value;
      }
    }

    handleOptionSelect(option) {
      const step = option.closest('.croev-quiz__step');
      const questionId = step.dataset.questionId;
      const selectionType = step.dataset.selectionType;
      const autoAdvance = step.dataset.autoAdvance === 'true';
      const value = option.dataset.optionValue;
      const label = option.dataset.optionLabel;

      if (selectionType === 'single') {
        // Deselect all others
        step.querySelectorAll('.croev-quiz__option').forEach(opt => {
          opt.classList.remove('is-selected');
          opt.setAttribute('aria-pressed', 'false');
        });
        
        // Select this one
        option.classList.add('is-selected');
        option.setAttribute('aria-pressed', 'true');
        
        // Store answer
        this.state.answers[questionId] = { value, label };
        this.saveState();
        
        // Auto-advance if enabled
        if (autoAdvance) {
          setTimeout(() => this.nextStep(), 300);
        } else {
          // Enable continue button
          const continueBtn = step.querySelector('[data-action="continue"]');
          if (continueBtn) continueBtn.disabled = false;
        }
      } else {
        // Multi-select toggle
        option.classList.toggle('is-selected');
        const isSelected = option.classList.contains('is-selected');
        option.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
        
        // Gather all selected values
        const selectedOptions = Array.from(step.querySelectorAll('.croev-quiz__option.is-selected'));
        this.state.answers[questionId] = selectedOptions.map(opt => ({
          value: opt.dataset.optionValue,
          label: opt.dataset.optionLabel
        }));
        this.saveState();
        
        // Enable/disable continue button based on selection
        const continueBtn = step.querySelector('[data-action="continue"]');
        if (continueBtn) {
          continueBtn.disabled = selectedOptions.length === 0;
        }
      }
    }

    showStep(index) {
      // Clear any pending timers
      this.clearTimers();
      
      // Hide all steps
      this.steps.forEach((step, i) => {
        if (i === index) {
          step.classList.add('is-active');
          step.classList.remove('is-exiting');
        } else {
          step.classList.remove('is-active');
        }
      });

      const currentStep = this.steps[index];
      if (!currentStep) return;

      const stepType = currentStep.dataset.stepType;

      // Focus management - only auto-focus input fields, not options/buttons
      setTimeout(() => {
        const inputField = currentStep.querySelector('.croev-quiz__input');
        if (inputField) inputField.focus();
      }, 100);

      // Restore selections for question steps
      if (stepType === 'question_image' || stepType === 'question_text') {
        this.restoreSelections(currentStep);
      }

      // Restore and handle input question steps
      if (stepType === 'question_input') {
        this.restoreInputValue(currentStep);
      }

      // Handle dynamic content for summary steps
      if (stepType === 'summary_profile') {
        this.populateSummaryProfile(currentStep);
      }

      if (stepType === 'summary_results') {
        this.populateSummaryResults(currentStep);
      }

      if (stepType === 'summary_comparison') {
        this.populateSummaryComparison(currentStep);
      }

      // Handle loading steps
      if (stepType === 'loading_step') {
        this.handleLoadingStep(currentStep);
      }

      // Handle info steps with dynamic content
      if (stepType === 'info_step') {
        this.populateInfoStep(currentStep);
      }

      // Handle offer step personalization
      if (stepType === 'offer_step') {
        this.populateOfferStep(currentStep);
      }

      // Update progress bar
      this.updateProgress();

      // Scroll to top
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    restoreSelections(step) {
      const questionId = step.dataset.questionId;
      const answer = this.state.answers[questionId];
      
      if (!answer) return;

      const isMulti = step.dataset.selectionType === 'multi';
      const values = isMulti ? answer.map(a => a.value) : [answer.value];

      step.querySelectorAll('.croev-quiz__option').forEach(option => {
        const optValue = option.dataset.optionValue;
        if (values.includes(optValue)) {
          option.classList.add('is-selected');
          option.setAttribute('aria-pressed', 'true');
        }
      });

      // Enable continue button if has selection
      const continueBtn = step.querySelector('[data-action="continue"]');
      if (continueBtn && values.length > 0) {
        continueBtn.disabled = false;
      }
    }

    restoreInputValue(step) {
      const questionId = step.dataset.questionId;
      const answer = this.state.answers[questionId];
      const input = step.querySelector('.croev-quiz__input');

      if (!answer || !input) return;

      input.value = answer.value || '';

      // Trigger validation to enable continue button if valid
      if (input.value) {
        this.handleInputChange(input);
      }
    }

    nextStep() {
      if (this.state.currentStep < this.steps.length - 1) {
        this.state.history.push(this.state.currentStep);
        this.state.currentStep++;
        this.showStep(this.state.currentStep);
        this.updateProgress();
        this.saveState();
      }
    }

    prevStep() {
      if (this.state.history.length > 0) {
        this.state.currentStep = this.state.history.pop();
        this.showStep(this.state.currentStep);
        this.updateProgress();
        this.saveState();
      }
    }

    updateProgress() {
      // Always re-query to ensure we have the element
      const bar = this.container.querySelector('.croev-quiz__progress-bar')
        || document.querySelector('.croev-quiz__progress-bar');
      const wrapper = this.container.querySelector('.croev-quiz__progress')
        || document.querySelector('.croev-quiz__progress');

      if (!bar) return;

      const progress = (this.state.currentStep / Math.max(this.steps.length - 1, 1)) * 100;
      bar.style.setProperty('width', `${progress}%`, 'important');

      if (wrapper) {
        wrapper.setAttribute('aria-valuenow', Math.round(progress));
      }
    }

    // --- Dynamic Content Population ---

    getPerceivedAge() {
      // Prefer exact age from input, fall back to Q1 age range
      const exactAge = this.state.answers.exact_age;
      const ageRangeAnswer = this.state.answers.age;

      let baseAge;
      if (exactAge && exactAge.value) {
        baseAge = parseInt(exactAge.value, 10);
      } else if (ageRangeAnswer) {
        baseAge = AGE_MAP[ageRangeAnswer.value] || 45;
      } else {
        return null;
      }

      // Perceived age = actual age + 10 years (skin makes them look older)
      return baseAge + 10;
    }

    getGoalAge() {
      // Prefer exact age from input, fall back to Q1 age range
      const exactAge = this.state.answers.exact_age;
      const ageRangeAnswer = this.state.answers.age;

      let baseAge;
      if (exactAge && exactAge.value) {
        baseAge = parseInt(exactAge.value, 10);
      } else if (ageRangeAnswer) {
        baseAge = AGE_MAP[ageRangeAnswer.value] || 45;
      } else {
        return null;
      }

      // Goal age = actual age - 8 years (look younger than their age)
      return baseAge - 8;
    }

    getProblemAreasText(limit = 2) {
      const problemAreas = this.state.answers.problem_areas;
      if (!problemAreas || !Array.isArray(problemAreas)) return 'your concerns';
      
      const labels = problemAreas.slice(0, limit).map(p => PROBLEM_LABELS[p.value] || p.label);
      return labels.join(' & ');
    }

    getDatePlus(days) {
      const date = new Date();
      date.setDate(date.getDate() + days);
      return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    }

    getSeverityData() {
      const severityAnswer = this.state.answers.severity;
      const skinAppearance = this.state.answers.skin_appearance;
      const severityValue = severityAnswer ? severityAnswer.value : null;
      const appearanceValue = skinAppearance ? skinAppearance.value : null;

      // Compute severity based on both severity answer and skin appearance
      // Prioritize the explicit severity question, use appearance as modifier
      const severityMap = {
        'mild': { badge: 'MODERATE', progress: 35, level: 'Mild', inflammation: 'Low', recovery: 'Very High' },
        'moderate': { badge: 'HIGH', progress: 60, level: 'Moderate', inflammation: 'Moderate', recovery: 'High' },
        'severe': { badge: 'VERY HIGH', progress: 85, level: 'Severe', inflammation: 'High', recovery: 'High' }
      };

      // If skin appearance is worse than severity answer, bump up
      let data = severityMap[severityValue] || severityMap['moderate'];

      if (appearanceValue === 'cystic' || appearanceValue === 'severe') {
        if (severityValue === 'mild') {
          data = { badge: 'HIGH', progress: 55, level: 'Moderate-High', inflammation: 'Moderate', recovery: 'High' };
        } else if (severityValue === 'moderate') {
          data = { badge: 'HIGH', progress: 70, level: 'High', inflammation: 'High', recovery: 'High' };
        }
      }

      return data;
    }

    populateSummaryProfile(step) {
      const problemAreasText = this.getProblemAreasText();
      const severityData = this.getSeverityData();

      // Update badge text dynamically
      const badgeEl = step.querySelector('[data-field="badge_text"]');
      if (badgeEl) {
        badgeEl.textContent = severityData.badge;
      }

      // Update progress marker position
      const markerEl = step.querySelector('[data-field="progress_marker"]');
      if (markerEl) {
        markerEl.style.left = `${severityData.progress}%`;
      }

      // Update severity level
      const severityEl = step.querySelector('[data-field="severity_level"]');
      if (severityEl) {
        severityEl.textContent = severityData.level;
      }

      // Update inflammation level
      const inflammationEl = step.querySelector('[data-field="inflammation_level"]');
      if (inflammationEl) {
        inflammationEl.textContent = severityData.inflammation;
      }

      // Update recovery potential
      const recoveryEl = step.querySelector('[data-field="recovery_potential"]');
      if (recoveryEl) {
        recoveryEl.textContent = severityData.recovery;
      }

      // Update problem areas
      const problemEl = step.querySelector('[data-field="problem_areas"]');
      if (problemEl) {
        problemEl.textContent = problemAreasText;
      }

      // Update alert message
      const alertEl = step.querySelector('.croev-quiz__alert');
      if (alertEl) {
        const template = alertEl.dataset.alertTemplate || '';
        const message = template
          .replace('{severity_level}', severityData.level)
          .replace('{problem_areas}', problemAreasText);
        const alertP = alertEl.querySelector('p');
        if (alertP) alertP.innerHTML = message;
      }

      // Update problem areas visual
      const problemAreasVisual = step.querySelector('.croev-quiz__problem-areas-visual');
      const problemAreas = this.state.answers.problem_areas;

      console.log('Problem areas visual element:', problemAreasVisual);
      console.log('Problem areas answers:', problemAreas);
      console.log('Dataset:', problemAreasVisual?.dataset);

      if (problemAreasVisual && problemAreas && Array.isArray(problemAreas) && problemAreas.length > 0) {
        // Show up to 3 problem area images
        const selectedAreas = problemAreas.slice(0, 3);
        let html = '<div class="croev-quiz__problem-areas-grid">';

        // Direct mapping for dataset keys (simplified attribute names without underscores)
        const imageMap = {
          'forehead': problemAreasVisual.dataset.imgForehead,
          'crows_feet': problemAreasVisual.dataset.imgCrowsfeet,
          'dark_circles': problemAreasVisual.dataset.imgDarkcircles,
          'smile_lines': problemAreasVisual.dataset.imgSmilelines,
          'sagging': problemAreasVisual.dataset.imgSagging,
          'uneven_tone': problemAreasVisual.dataset.imgUneventone
        };

        const labelMap = {
          'forehead': 'Forehead Lines',
          'crows_feet': "Crow's Feet",
          'dark_circles': 'Dark Circles',
          'smile_lines': 'Smile Lines',
          'sagging': 'Sagging',
          'uneven_tone': 'Uneven Tone'
        };

        console.log('Image map:', imageMap);

        selectedAreas.forEach(area => {
          const imageUrl = imageMap[area.value];
          const label = labelMap[area.value] || PROBLEM_LABELS[area.value] || area.label;

          console.log(`Area: ${area.value}, Image URL: ${imageUrl}`);

          if (imageUrl && imageUrl.length > 10) {
            html += `
              <div class="croev-quiz__problem-area-item">
                <img src="${imageUrl}" alt="${label}" class="croev-quiz__problem-area-img" loading="lazy">
                <span class="croev-quiz__problem-area-label">${label}</span>
              </div>
            `;
          }
        });

        html += '</div>';
        problemAreasVisual.innerHTML = html;
      } else {
        console.log('Skipping problem areas visual - conditions not met');
      }
    }

    populateSummaryResults(step) {
      const date30 = this.getDatePlus(30);
      const date60 = this.getDatePlus(60);

      step.querySelectorAll('[data-template]').forEach(el => {
        const template = el.dataset.template;
        const content = template
          .replace('{date_30}', date30)
          .replace('{date_60}', date60);
        el.innerHTML = content;
      });
    }

    populateSummaryComparison(step) {
      const perceivedAge = this.getPerceivedAge();
      const goalAge = this.getGoalAge();
      const ageAnswer = this.state.answers.age?.value;

      // Update ages
      const perceivedAgeEl = step.querySelector('[data-field="perceived_age"]');
      if (perceivedAgeEl && perceivedAge) {
        perceivedAgeEl.textContent = `${perceivedAge} years`;
      }

      const goalAgeEl = step.querySelector('[data-field="goal_age"]');
      if (goalAgeEl && goalAge) {
        goalAgeEl.textContent = `${goalAge} years`;
      }

      // Update years younger text
      const yearsYoungerEl = step.querySelector('[data-field="years_younger"]');
      if (yearsYoungerEl && perceivedAge && goalAge) {
        const yearsDiff = perceivedAge - goalAge;
        yearsYoungerEl.textContent = `${yearsDiff}+ years`;
      }

      // Update images using age images from quiz config
      const ageImages = this.config.ageImages || {};
      const nowImageUrl = ageAnswer ? ageImages[ageAnswer] : null;

      if (nowImageUrl && nowImageUrl.length > 0) {
        // NOW image = user's selected age
        const nowImg = step.querySelector('[data-image-type="now"]');
        if (nowImg) {
          nowImg.style.backgroundImage = `url('${nowImageUrl}')`;
          // Remove placeholder
          const placeholder = nowImg.querySelector('.croev-quiz__comparison-card-placeholder');
          if (placeholder) placeholder.style.display = 'none';
        }

        // GOAL image = younger age bracket (2 steps younger, min is under_35)
        // If already at youngest tier (under_35), use special "youngest_goal" image
        const currentAgeIndex = AGE_ORDER.indexOf(ageAnswer);
        const goalAgeIndex = Math.max(0, currentAgeIndex - 2);
        let goalImageUrl;

        if (currentAgeIndex === 0) {
          // User selected youngest tier - use the special youngest goal image
          goalImageUrl = ageImages['youngest_goal'];
        } else {
          const goalAgeKey = AGE_ORDER[goalAgeIndex];
          goalImageUrl = ageImages[goalAgeKey];
        }

        const goalImg = step.querySelector('[data-image-type="goal"]');
        if (goalImg && goalImageUrl && goalImageUrl.length > 0) {
          goalImg.style.backgroundImage = `url('${goalImageUrl}')`;
          // Remove placeholder
          const placeholder = goalImg.querySelector('.croev-quiz__comparison-card-placeholder');
          if (placeholder) placeholder.style.display = 'none';
        }
      }
    }

    populateInfoStep(step) {
      // Handle dynamic subheadline with {problem_areas} placeholder
      const subheadlineEl = step.querySelector('.croev-quiz__subheadline--dynamic');
      if (subheadlineEl) {
        const template = subheadlineEl.dataset.subheadlineTemplate || subheadlineEl.textContent;
        const problemAreasText = this.getProblemAreasTextBold();
        const content = template.replace('{problem_areas}', problemAreasText);
        subheadlineEl.innerHTML = content;
      }

      // Duplicate carousel images for seamless infinite scroll
      const carousel = step.querySelector('.croev-quiz__image-carousel');
      if (carousel && !carousel.dataset.duplicated) {
        const track = carousel.querySelector('.croev-quiz__image-carousel-track');
        if (track) {
          const items = track.innerHTML;
          track.innerHTML = items + items; // Duplicate for seamless loop
          carousel.dataset.duplicated = 'true';
        }
      }

      // Arrow navigation for image carousel (desktop)
      if (carousel) {
        const track = carousel.querySelector('.croev-quiz__image-carousel-track');
        const prevBtn = carousel.querySelector('[data-carousel-arrow="prev"]');
        const nextBtn = carousel.querySelector('[data-carousel-arrow="next"]');
        if (track && prevBtn && nextBtn) {
          const scrollAmount = () => {
            const item = track.querySelector('.croev-quiz__image-carousel-item');
            return item ? item.offsetWidth + 12 : 300;
          };
          prevBtn.addEventListener('click', () => {
            track.scrollBy({ left: -scrollAmount(), behavior: 'smooth' });
          });
          nextBtn.addEventListener('click', () => {
            track.scrollBy({ left: scrollAmount(), behavior: 'smooth' });
          });
        }
      }
    }

    populateOfferStep(step) {
      const answers = this.state.answers;
      const recommendationName = step.dataset.recommendationName || '';
      const benefitDailyCost = step.dataset.benefitDailyCost || '';

      // Personalized subheadline
      const personalizedEl = step.querySelector('[data-offer-personalized]');
      if (personalizedEl) {
        const concerns = this.getProblemAreasTextBold(3);
        const severity = answers.severity;
        const severityLabel = severity ? severity.label : '';

        let text;
        if (recommendationName) {
          text = `Based on your <strong>${severityLabel || 'acne'}</strong> with ${concerns}, we recommend ${recommendationName}.`;
        } else {
          text = `Based on your <strong>${severityLabel || 'acne'}</strong> concerns with ${concerns}, we recommend our complete 3-step ritual.`;
        }
        personalizedEl.innerHTML = text;
      }

      // Personalized benefits
      const benefitsEl = step.querySelector('[data-offer-benefits]');
      if (benefitsEl) {
        const checkSvg = '<svg class="croev-quiz__offer-benefit-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>';

        // Map concerns to specific benefits
        const benefitMap = {
          'teen_breakouts': 'Gentle enough for <strong>young skin</strong> — no harsh chemicals that damage your skin barrier',
          'persistent_acne': 'Targets <strong>persistent breakouts</strong> at the root with Arctic antibacterial plants',
          'inflamed_skin': 'Reduces <strong>redness & inflammation</strong> with wild-harvested Arctic botanicals',
          'hormonal_cystic': 'Penetrates deep to calm <strong>hormonal & cystic acne</strong> from within',
          'dark_marks': 'Fades <strong>dark marks & acne scars</strong> with natural brightening compounds',
          'confidence_acne': 'Visible improvement in <strong>as little as 3 days</strong> — confidence you can see'
        };

        // Default benefits that always show
        const defaultBenefits = [
          '100% natural <strong>wild-harvested Arctic ingredients</strong> — no harsh chemicals',
          'Complete 3-step system: <strong>Cleanse, Treat & Protect</strong>',
          '<strong>60-day money-back guarantee</strong> — zero risk to try'
        ];

        let benefits = [];

        // Add concern-specific benefits
        const problemAreas = answers.problem_areas;
        if (problemAreas && Array.isArray(problemAreas)) {
          problemAreas.slice(0, 3).forEach(p => {
            const benefit = benefitMap[p.value];
            if (benefit) benefits.push(benefit);
          });
        }

        // Add time-based benefit
        const dailyTime = answers.daily_time;
        if (dailyTime && dailyTime.value === 'under_2min') {
          benefits.push('Takes <strong>less than 2 minutes</strong> — fits your busy routine');
        } else if (dailyTime && dailyTime.value === '2_5min') {
          benefits.push('Simple <strong>3-step routine</strong> that fits easily into your day');
        }

        // Add sensitivity benefit
        const sensitive = answers.sensitive_skin;
        if (sensitive && sensitive.value === 'yes') {
          benefits.push('Formulated for <strong>sensitive skin</strong> — no irritation, no dryness');
        }

        // Fill with defaults if we don't have enough
        if (benefits.length < 3) {
          defaultBenefits.forEach(b => {
            if (benefits.length < 5 && !benefits.includes(b)) benefits.push(b);
          });
        }

        if (benefitDailyCost) {
          benefits.splice(3, 0, benefitDailyCost);
        }

        // Cap at 5
        benefits = benefits.slice(0, 5);

        let html = '<p class="croev-quiz__offer-benefits-title">Why this is perfect for you</p>';
        benefits.forEach(b => {
          html += `<div class="croev-quiz__offer-benefit">${checkSvg}<p class="croev-quiz__offer-benefit-text">${b}</p></div>`;
        });

        benefitsEl.innerHTML = html;
      }
    }

    getProblemAreasTextBold(limit = 3) {
      const problemAreas = this.state.answers.problem_areas;
      if (!problemAreas || !Array.isArray(problemAreas)) return '<strong>your concerns</strong>';

      const labels = problemAreas.slice(0, limit).map(p => PROBLEM_LABELS[p.value] || p.label);
      return '<strong>' + labels.join(' & ') + '</strong>';
    }

    handleLoadingStep(step) {
      const duration = parseInt(step.dataset.duration, 10) || 3000;
      const checklist = step.querySelector('.croev-quiz__checklist');

      if (checklist) {
        // Animate checklist items
        const items = Array.from(checklist.querySelectorAll('.croev-quiz__checklist-item'));
        items.forEach((item, i) => {
          const delay = parseInt(item.dataset.delay, 10) || i * 1000;
          
          const timer = setTimeout(() => {
            item.classList.add('is-visible');
            
            // Mark as complete after appearing
            setTimeout(() => {
              item.classList.add('is-complete');
            }, 500);
          }, delay);
          
          this.checklistTimers.push(timer);
        });
      }

      // Auto-advance after duration
      this.autoAdvanceTimer = setTimeout(() => {
        this.nextStep();
      }, duration);
    }

    clearTimers() {
      if (this.autoAdvanceTimer) {
        clearTimeout(this.autoAdvanceTimer);
        this.autoAdvanceTimer = null;
      }
      this.checklistTimers.forEach(t => clearTimeout(t));
      this.checklistTimers = [];
    }

    // --- Offer Handling ---

    handleSkip(btn) {
      // Skip this step without saving an answer
      const step = btn.closest('.croev-quiz__step');
      const questionId = step?.dataset.questionId;

      // Clear any answer for this question
      if (questionId && this.state.answers[questionId]) {
        delete this.state.answers[questionId];
        this.saveState();
      }

      // Move to next step
      this.nextStep();
    }

    handleClaimOffer(btn) {
      const productUrl = btn.dataset.productUrl;
      const variantId = btn.dataset.variantId;
      const step = btn.closest('.croev-quiz__step');
      const discountCode = step?.dataset.discountCode;

      // Build URL with quiz answers as parameters
      const params = new URLSearchParams();
      
      // Add all answers
      Object.entries(this.state.answers).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          params.set(key, value.map(v => v.value).join(','));
        } else if (value && value.value) {
          params.set(key, value.value);
        }
      });

      // Add variant if available
      if (variantId) {
        params.set('variant', variantId);
      }

      // Add discount code if available
      if (discountCode) {
        params.set('discount', discountCode);
      }

      // Redirect based on gender selection, then fallback
      const genderValue = this.state.answers.gender?.value;
      let url = '';
      if (genderValue === 'male' && this.redirectUrlMale) {
        url = this.redirectUrlMale;
      } else if (genderValue === 'female' && this.redirectUrlFemale) {
        url = this.redirectUrlFemale;
      } else if (this.redirectUrl) {
        url = this.redirectUrl;
      } else {
        url = productUrl;
      }
      if (url) {
        const separator = url.includes('?') ? '&' : '?';
        window.location.href = `${url}${separator}${params.toString()}`;
      }
    }

    // --- State Persistence ---

    saveState() {
      try {
        localStorage.setItem(`croev-quiz-${this.sectionId}`, JSON.stringify(this.state));
      } catch (e) {
        console.warn('Could not save quiz state:', e);
      }
    }

    loadState() {
      try {
        const saved = localStorage.getItem(`croev-quiz-${this.sectionId}`);
        if (saved) {
          const parsed = JSON.parse(saved);
          // Only restore answers, start from beginning
          this.state.answers = parsed.answers || {};
        }
      } catch (e) {
        console.warn('Could not load quiz state:', e);
      }
    }

    clearState() {
      try {
        localStorage.removeItem(`croev-quiz-${this.sectionId}`);
      } catch (e) {
        console.warn('Could not clear quiz state:', e);
      }
    }

    // --- Utilities ---

    capitalize(str) {
      return str.charAt(0).toUpperCase() + str.slice(1);
    }

    destroy() {
      this.clearTimers();
    }
  }

  // Initialize all quiz instances
  function initQuizzes() {
    document.querySelectorAll('[data-section-type="croev-quiz"]').forEach(container => {
      if (!container.croevQuiz) {
        container.croevQuiz = new CroevQuiz(container);
      }
    });
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initQuizzes);
  } else {
    initQuizzes();
  }

  // Handle Shopify theme editor events
  document.addEventListener('shopify:section:load', (e) => {
    const container = e.target.querySelector('[data-section-type="croev-quiz"]');
    if (container) {
      container.croevQuiz = new CroevQuiz(container);
    }
  });

  document.addEventListener('shopify:section:unload', (e) => {
    const container = e.target.querySelector('[data-section-type="croev-quiz"]');
    if (container && container.croevQuiz) {
      container.croevQuiz.destroy();
    }
  });

})();
